import { businessUnitsApi } from '../api/modules/businessUnits.api';
import { sitesApi } from '../api/modules/sites.api';
import { getApiErrorMessage } from '../api/client';
import { USE_API } from '../config/api';
import { businessUnitSeed, siteSeed } from '../data/master/businessUnits';
import {
  businessUnitFromApi,
  businessUnitToApi,
  mapPaginated,
  siteFromApi,
  siteToApi,
} from '../models';

export async function listBusinessUnits(params = {}) {
  if (!USE_API) {
    let items = [...businessUnitSeed];
    if (params.search) {
      const q = String(params.search).toLowerCase();
      items = items.filter(
        (b) =>
          b.shortName?.toLowerCase().includes(q) ||
          b.buId?.toLowerCase().includes(q) ||
          b.fullNameGst?.toLowerCase().includes(q),
      );
    }
    const page = params.page || 1;
    const pageSize = params.pageSize || items.length || 50;
    const start = (page - 1) * pageSize;
    return { items: items.slice(start, start + pageSize), total: items.length, page, pageSize };
  }
  try {
    return mapPaginated(await businessUnitsApi.list(params), businessUnitFromApi);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load business units'));
  }
}

export async function getBusinessUnit(id) {
  if (!USE_API) return businessUnitSeed.find((b) => b.buId === id || b.id === id) || null;
  try {
    return businessUnitFromApi(await businessUnitsApi.getById(id));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load business unit'));
  }
}

export async function createBusinessUnit(ui) {
  if (!USE_API) throw new Error('Create business unit requires API mode');
  try {
    return businessUnitFromApi(await businessUnitsApi.create(businessUnitToApi(ui)));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to create business unit'));
  }
}

export async function updateBusinessUnit(id, ui) {
  if (!USE_API) throw new Error('Update business unit requires API mode');
  try {
    return businessUnitFromApi(await businessUnitsApi.update(id, businessUnitToApi(ui)));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to update business unit'));
  }
}

export async function deleteBusinessUnit(id) {
  if (!USE_API) throw new Error('Delete business unit requires API mode');
  try {
    await businessUnitsApi.remove(id);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to delete business unit'));
  }
}

export async function listSites(params = {}) {
  if (!USE_API) {
    let items = [...siteSeed];
    if (params.buId) items = items.filter((s) => s.buId === params.buId || s.buDbId === params.buId);
    const page = params.page || 1;
    const pageSize = params.pageSize || items.length || 50;
    const start = (page - 1) * pageSize;
    return { items: items.slice(start, start + pageSize), total: items.length, page, pageSize };
  }
  try {
    if (params.buId && !params.search) {
      const sites = await businessUnitsApi.listSites(params.buId);
      const items = (Array.isArray(sites) ? sites : []).map(siteFromApi);
      return { items, total: items.length, page: 1, pageSize: items.length || 50 };
    }
    return mapPaginated(await sitesApi.list(params), siteFromApi);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load sites'));
  }
}

export async function createSite(ui) {
  if (!USE_API) throw new Error('Create site requires API mode');
  try {
    return siteFromApi(await sitesApi.create(siteToApi(ui)));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to create site'));
  }
}

export async function updateSite(id, ui) {
  if (!USE_API) throw new Error('Update site requires API mode');
  try {
    return siteFromApi(await sitesApi.update(id, siteToApi(ui)));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to update site'));
  }
}

export async function deleteSite(id) {
  if (!USE_API) throw new Error('Delete site requires API mode');
  try {
    await sitesApi.remove(id);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to delete site'));
  }
}
